import type { JSX, ParentProps } from 'solid-js';
import { For, Show, createEffect, createUniqueId, splitProps } from 'solid-js';
import * as KobalteDialog from '@kobalte/core/dialog';
import * as KobalteAlertDialog from '@kobalte/core/alert-dialog';
import * as KobalteTabs from '@kobalte/core/tabs';
import styles from './ui.module.css';
import './tokens.css';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type Density = 'comfortable' | 'compact';
export type StatusTone = 'neutral' | 'success' | 'warning' | 'error' | 'info';

export type ButtonProps = ParentProps<
  JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant | undefined;
    density?: Density | undefined;
    pending?: boolean | undefined;
    pendingLabel?: string | undefined;
  }
>;

export const Button = (props: ButtonProps) => {
  const [local, buttonProps] = splitProps(props, [
    'variant',
    'density',
    'pending',
    'pendingLabel',
    'children',
    'class',
    'disabled',
  ]);
  return (
    <button
      {...buttonProps}
      aria-busy={local.pending ? true : undefined}
      disabled={local.disabled ? true : local.pending}
      class={`${styles.button} ${styles[local.variant ?? 'primary']} ${styles[local.density ?? 'comfortable']} ${local.class ?? ''}`.trim()}
    >
      {local.pending ? (local.pendingLabel ?? local.children) : local.children}
    </button>
  );
};

/**
 * Semantic pass-through controls for route-owned compositions whose dynamic options, bespoke
 * visual treatment, or native form contract do not fit the labeled Field composites below. These
 * keep the application boundary typed and library-neutral without imposing presentation styles.
 */
export const UnstyledButton = (props: JSX.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button {...props} />
);

export const InputControl = (props: JSX.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} />
);

export const SelectControl = (props: ParentProps<JSX.SelectHTMLAttributes<HTMLSelectElement>>) => (
  <select {...props} />
);

export const TextAreaControl = (
  props: ParentProps<JSX.TextareaHTMLAttributes<HTMLTextAreaElement>>,
) => <textarea {...props} />;

type TabsRootProps = ParentProps<{
  class?: string | undefined;
  defaultValue: string;
}>;
type TabsListProps = ParentProps<{ class?: string | undefined; 'aria-label': string }>;
type TabsTriggerProps = ParentProps<{ class?: string | undefined; value: string }>;
type TabsContentProps = ParentProps<{ class?: string | undefined; value: string }>;

const TabsRoot = (props: TabsRootProps) => (
  <KobalteTabs.Root class={props.class} defaultValue={props.defaultValue}>
    {props.children}
  </KobalteTabs.Root>
);
const TabsList = (props: TabsListProps) => (
  <KobalteTabs.List class={props.class} aria-label={props['aria-label']}>
    {props.children}
  </KobalteTabs.List>
);
const TabsTrigger = (props: TabsTriggerProps) => (
  <KobalteTabs.Trigger class={props.class} value={props.value}>
    {props.children}
  </KobalteTabs.Trigger>
);
const TabsIndicator = (props: { class?: string | undefined }) => (
  <KobalteTabs.Indicator class={props.class} />
);
const TabsContent = (props: TabsContentProps) => (
  <KobalteTabs.Content class={props.class} value={props.value}>
    {props.children}
  </KobalteTabs.Content>
);

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Indicator: TabsIndicator,
  Content: TabsContent,
});

export type IconName =
  | 'menu'
  | 'overview'
  | 'people'
  | 'groups'
  | 'curriculum'
  | 'schedule'
  | 'presentations'
  | 'deliveries'
  | 'learning'
  | 'assessment'
  | 'messages'
  | 'privacy'
  | 'access'
  | 'operations'
  | 'governance'
  | 'search'
  | 'close'
  | 'warning'
  | 'archive';

const iconPath = (name: IconName) => {
  switch (name) {
    case 'menu':
      return <path d="M4 7h16M4 12h16M4 17h16" />;
    case 'overview':
      return <path d="M3 10.5 12 3l9 7.5M5.5 9.5V21h13V9.5M9 21v-7h6v7" />;
    case 'people':
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      );
    case 'groups':
      return (
        <>
          <circle cx="8" cy="8" r="3" />
          <circle cx="17" cy="8" r="3" />
          <path d="M2 21v-2a5 5 0 0 1 10 0v2M12 19a5 5 0 0 1 10 0v2" />
        </>
      );
    case 'curriculum':
      return (
        <>
          <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H20v17H7.5A3.5 3.5 0 0 0 4 22.5z" />
          <path d="M4 5.5v17M8 7h8M8 11h8" />
        </>
      );
    case 'schedule':
      return (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18M7 14h3M14 14h3M7 18h3" />
        </>
      );
    case 'presentations':
      return (
        <>
          <rect x="3" y="3" width="18" height="14" rx="2" />
          <path d="m8 21 4-4 4 4M8 8h8M8 12h5" />
        </>
      );
    case 'deliveries':
      return (
        <>
          <path d="m5 3 14 9-14 9z" />
          <path d="M5 12h14" />
        </>
      );
    case 'learning':
      return (
        <>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z" />
          <path d="m9 10 2 2 4-4" />
        </>
      );
    case 'assessment':
      return (
        <>
          <path d="M9 5h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H9" />
          <path d="M5 3h8v14H5zM8 7h2M8 11h2" />
        </>
      );
    case 'messages':
      return <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />;
    case 'privacy':
      return (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
          <path d="m9 12 2 2 4-4" />
        </>
      );
    case 'access':
      return (
        <>
          <circle cx="8" cy="8" r="4" />
          <path d="M2 22v-2a6 6 0 0 1 12 0v2M16 11h6M19 8v6" />
        </>
      );
    case 'operations':
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.16.38.37.72.6 1 .3.37.7.58 1.1.6H21v4h-.09A1.7 1.7 0 0 0 19.4 15" />
        </>
      );
    case 'governance':
      return (
        <>
          <path d="M3 10h18M5 10v9M9 10v9M15 10v9M19 10v9M2 21h20M12 3 2 8h20z" />
        </>
      );
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </>
      );
    case 'close':
      return <path d="m6 6 12 12M18 6 6 18" />;
    case 'warning':
      return (
        <>
          <path d="M10.3 3.6 2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0" />
          <path d="M12 9v4M12 17h.01" />
        </>
      );
    case 'archive':
      return (
        <>
          <path d="M3 6h18M5 6v15h14V6M2 3h20v3H2zM9 11h6" />
        </>
      );
  }
};

export const Icon = (props: {
  name: IconName;
  size?: 16 | 20 | 24;
  label?: string;
  class?: string;
}) => (
  <svg
    class={`${styles.icon} ${props.class ?? ''}`.trim()}
    width={props.size ?? 20}
    height={props.size ?? 20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    role={props.label ? 'img' : undefined}
    aria-label={props.label}
    aria-hidden={props.label ? undefined : 'true'}
  >
    {iconPath(props.name)}
  </svg>
);

export type IconButtonProps = Omit<ButtonProps, 'children'> & {
  icon: IconName;
  label: string;
  tooltip?: string;
};

export const IconButton = (props: IconButtonProps) => {
  const [local, buttonProps] = splitProps(props, ['icon', 'label', 'tooltip']);
  return (
    <Button
      {...buttonProps}
      class={`${styles.iconButton} ${buttonProps.class ?? ''}`.trim()}
      aria-label={local.label}
      title={local.tooltip ?? local.label}
    >
      <Icon name={local.icon} />
    </Button>
  );
};

export type SurfaceProps = ParentProps<
  JSX.HTMLAttributes<HTMLElement> & { elevation?: 'flat' | 'raised' | 'inset' }
>;

export const Surface = (props: SurfaceProps) => {
  const [local, surfaceProps] = splitProps(props, ['elevation', 'class']);
  return (
    <section
      {...surfaceProps}
      class={`${styles.surface} ${styles[local.elevation ?? 'flat']} ${local.class ?? ''}`.trim()}
    />
  );
};

export const Card = (props: ParentProps<JSX.HTMLAttributes<HTMLElement>>) => (
  <Surface {...props} class={`${styles.card} ${props.class ?? ''}`.trim()} />
);

export const Status = (props: { tone?: StatusTone; children: JSX.Element; class?: string }) => (
  <span class={`${styles.pill} ${styles[props.tone ?? 'neutral']} ${props.class ?? ''}`.trim()}>
    {props.children}
  </span>
);

export const StatusPill = Status;

export type FieldProps = ParentProps<{
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  class?: string | undefined;
}>;

export const Field = (props: FieldProps) => {
  const hintId = () => (props.hint ? `${props.id}-hint` : undefined);
  const errorId = () => (props.error ? `${props.id}-error` : undefined);
  return (
    <div
      class={`${styles.field} ${props.error ? styles.fieldInvalid : ''} ${props.class ?? ''}`.trim()}
    >
      <label for={props.id}>
        {props.label}
        <Show when={props.required}>
          <span aria-hidden="true"> *</span>
        </Show>
      </label>
      {props.children}
      <Show when={props.hint}>
        <p id={hintId()} class={styles.hint}>
          {props.hint}
        </p>
      </Show>
      <Show when={props.error}>
        <p id={errorId()} class={styles.fieldError} role="alert">
          {props.error}
        </p>
      </Show>
    </div>
  );
};

export type TextFieldProps = Omit<
  JSX.InputHTMLAttributes<HTMLInputElement>,
  'children' | 'onInput'
> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  density?: Density | undefined;
  onValueChange?: ((value: string) => void) | undefined;
};

export const TextField = (props: TextFieldProps) => {
  const generatedId = createUniqueId();
  const [local, inputProps] = splitProps(props, [
    'id',
    'label',
    'hint',
    'error',
    'required',
    'class',
    'density',
    'onValueChange',
  ]);
  const id = () => local.id ?? generatedId;
  const describedBy = () =>
    [local.hint && `${id()}-hint`, local.error && `${id()}-error`].filter(Boolean).join(' ') ||
    undefined;
  return (
    <Field
      id={id()}
      label={local.label}
      hint={local.hint}
      error={local.error}
      required={local.required}
      class={local.class}
    >
      <input
        {...inputProps}
        id={id()}
        required={local.required}
        aria-invalid={Boolean(local.error) || undefined}
        aria-describedby={describedBy()}
        class={`${styles.input} ${styles[local.density ?? 'comfortable']}`}
        onInput={(event) => {
          local.onValueChange?.(event.currentTarget.value);
        }}
      />
    </Field>
  );
};

export const SearchField = (props: TextFieldProps) => (
  <TextField {...props} type="search" autocomplete={props.autocomplete ?? 'off'} />
);

export type TextAreaFieldProps = Omit<
  JSX.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onInput'
> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  density?: Density | undefined;
  onValueChange?: ((value: string) => void) | undefined;
};

export const TextAreaField = (props: TextAreaFieldProps) => {
  const generatedId = createUniqueId();
  const [local, textAreaProps] = splitProps(props, [
    'id',
    'label',
    'hint',
    'error',
    'required',
    'class',
    'density',
    'onValueChange',
  ]);
  const id = () => local.id ?? generatedId;
  const describedBy = () =>
    [local.hint && `${id()}-hint`, local.error && `${id()}-error`].filter(Boolean).join(' ') ||
    undefined;
  return (
    <Field
      id={id()}
      label={local.label}
      hint={local.hint}
      error={local.error}
      required={local.required}
      class={local.class}
    >
      <textarea
        {...textAreaProps}
        id={id()}
        required={local.required}
        aria-invalid={Boolean(local.error) || undefined}
        aria-describedby={describedBy()}
        class={`${styles.input} ${styles.textarea} ${styles[local.density ?? 'comfortable']}`}
        onInput={(event) => {
          local.onValueChange?.(event.currentTarget.value);
        }}
      />
    </Field>
  );
};

export type SelectOption = { value: string; label: string; disabled?: boolean };
export type SelectFieldProps = Omit<
  JSX.SelectHTMLAttributes<HTMLSelectElement>,
  'children' | 'onInput'
> & {
  label: string;
  options: readonly SelectOption[];
  hint?: string | undefined;
  error?: string | undefined;
  density?: Density | undefined;
  onValueChange?: ((value: string) => void) | undefined;
};

export const SelectField = (props: SelectFieldProps) => {
  const generatedId = createUniqueId();
  const [local, selectProps] = splitProps(props, [
    'id',
    'label',
    'options',
    'hint',
    'error',
    'required',
    'class',
    'density',
    'onValueChange',
  ]);
  const id = () => local.id ?? generatedId;
  const describedBy = () =>
    [local.hint && `${id()}-hint`, local.error && `${id()}-error`].filter(Boolean).join(' ') ||
    undefined;
  return (
    <Field
      id={id()}
      label={local.label}
      hint={local.hint}
      error={local.error}
      required={local.required}
      class={local.class}
    >
      <select
        {...selectProps}
        id={id()}
        required={local.required}
        aria-invalid={Boolean(local.error) || undefined}
        aria-describedby={describedBy()}
        class={`${styles.input} ${styles[local.density ?? 'comfortable']}`}
        onInput={(event) => {
          local.onValueChange?.(event.currentTarget.value);
        }}
      >
        <For each={local.options}>
          {(option) => (
            <option value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          )}
        </For>
      </select>
    </Field>
  );
};

export const EmptyState = (props: { title: string; description: string; action?: JSX.Element }) => (
  <div class={styles.empty}>
    <h2>{props.title}</h2>
    <p>{props.description}</p>
    {props.action}
  </div>
);

export const Notice = (props: {
  tone: Exclude<StatusTone, 'neutral'>;
  title: string;
  children: JSX.Element;
}) => (
  <div
    class={`${styles.notice} ${styles[props.tone]}`}
    role={props.tone === 'error' ? 'alert' : 'status'}
  >
    <strong>{props.title}</strong>
    {props.children}
  </div>
);

export type QueryStateProps = {
  state: 'loading' | 'empty' | 'error' | 'stale';
  label: string;
  description?: string;
  retry?: () => void;
};

export const QueryState = (props: QueryStateProps) => (
  <Show
    when={props.state !== 'loading'}
    fallback={
      <div class={styles.querySkeleton} role="status" aria-label={`Loading ${props.label}`}>
        <span />
        <span />
        <span />
      </div>
    }
  >
    <Show
      when={props.state !== 'empty'}
      fallback={
        <EmptyState
          title={`No ${props.label}`}
          description={props.description ?? 'Nothing is available in the current authorized scope.'}
        />
      }
    >
      <Notice
        tone={props.state === 'error' ? 'error' : 'warning'}
        title={
          props.state === 'error'
            ? `${props.label} unavailable`
            : `${props.label} may be out of date`
        }
      >
        <p>
          {props.description ??
            (props.state === 'error'
              ? 'The current data could not be loaded.'
              : 'Reconnect or refresh before making a consequential change.')}
        </p>
        <Show when={props.retry}>
          <Button variant="secondary" onClick={props.retry}>
            Retry
          </Button>
        </Show>
      </Notice>
    </Show>
  </Show>
);

export type DialogProps = ParentProps<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  footer?: JSX.Element;
  initialFocusRef?: () => HTMLElement | undefined;
}>;

export const Dialog = (props: DialogProps) => {
  let returnFocus: HTMLElement | undefined;
  let wasOpen = false;
  createEffect(() => {
    if (props.open && !wasOpen) {
      returnFocus =
        document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    }
    if (!props.open && wasOpen && returnFocus) {
      queueMicrotask(() => returnFocus?.focus());
    }
    wasOpen = props.open;
  });
  return (
    <KobalteDialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <KobalteDialog.Portal>
        <KobalteDialog.Overlay class={styles.dialogOverlay} />
        <div class={styles.dialogPositioner}>
          <KobalteDialog.Content
            class={styles.dialogContent}
            onOpenAutoFocus={(event) => {
              const target = props.initialFocusRef?.();
              if (!target) return;
              event.preventDefault();
              target.focus();
            }}
          >
            <div class={styles.dialogHeader}>
              <KobalteDialog.Title class={styles.dialogTitle}>{props.title}</KobalteDialog.Title>
              <KobalteDialog.CloseButton class={styles.dialogClose} aria-label="Close dialog">
                <Icon name="close" />
              </KobalteDialog.CloseButton>
            </div>
            <Show when={props.description}>
              <KobalteDialog.Description class={styles.dialogDescription}>
                {props.description}
              </KobalteDialog.Description>
            </Show>
            <div class={styles.dialogBody}>{props.children}</div>
            <Show when={props.footer}>
              <div class={styles.dialogFooter}>{props.footer}</div>
            </Show>
          </KobalteDialog.Content>
        </div>
      </KobalteDialog.Portal>
    </KobalteDialog.Root>
  );
};

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
};

export const ConfirmDialog = (props: ConfirmDialogProps) => {
  let returnFocus: HTMLElement | undefined;
  let wasOpen = false;
  createEffect(() => {
    if (props.open && !wasOpen) {
      returnFocus =
        document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    }
    if (!props.open && wasOpen && returnFocus) {
      queueMicrotask(() => returnFocus?.focus());
    }
    wasOpen = props.open;
  });
  return (
    <KobalteAlertDialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <KobalteAlertDialog.Portal>
        <KobalteAlertDialog.Overlay class={styles.dialogOverlay} />
        <div class={styles.dialogPositioner}>
          <KobalteAlertDialog.Content class={styles.dialogContent}>
            <KobalteAlertDialog.Title class={styles.dialogTitle}>
              {props.title}
            </KobalteAlertDialog.Title>
            <KobalteAlertDialog.Description class={styles.dialogDescription}>
              {props.description}
            </KobalteAlertDialog.Description>
            <div class={styles.dialogFooter}>
              <KobalteAlertDialog.CloseButton
                as={Button}
                variant="secondary"
                aria-label={props.cancelLabel ?? 'Cancel'}
              >
                {props.cancelLabel ?? 'Cancel'}
              </KobalteAlertDialog.CloseButton>
              <Button
                variant={props.danger ? 'danger' : 'primary'}
                pending={props.pending}
                onClick={props.onConfirm}
              >
                {props.confirmLabel}
              </Button>
            </div>
          </KobalteAlertDialog.Content>
        </div>
      </KobalteAlertDialog.Portal>
    </KobalteAlertDialog.Root>
  );
};

export const ToastRegion = (props: ParentProps<{ label?: string }>) => (
  <section
    class={styles.toastRegion}
    aria-label={props.label ?? 'Notifications'}
    aria-live="polite"
    aria-relevant="additions text"
  >
    {props.children}
  </section>
);

export type DataListProps<T> = {
  items: readonly T[];
  getKey: (item: T) => string | number;
  renderItem: (item: T, index: () => number) => JSX.Element;
  label: string;
  empty?: JSX.Element;
  class?: string | undefined;
};

export const DataList = <T,>(props: DataListProps<T>) => (
  <Show
    when={props.items.length > 0}
    fallback={
      props.empty ?? (
        <EmptyState
          title={`No ${props.label}`}
          description="Nothing matches the current authorized scope."
        />
      )
    }
  >
    <ul class={`${styles.dataList} ${props.class ?? ''}`.trim()} aria-label={props.label}>
      <For each={props.items}>
        {(item, index) => <li data-key={props.getKey(item)}>{props.renderItem(item, index)}</li>}
      </For>
    </ul>
  </Show>
);

export const FilterBar = (
  props: ParentProps<{
    label?: string;
    summary?: JSX.Element;
    class?: string | undefined;
  }>,
) => (
  <section
    class={`${styles.filterBar} ${props.class ?? ''}`.trim()}
    aria-label={props.label ?? 'View controls'}
  >
    <div class={styles.filterControls}>{props.children}</div>
    <Show when={props.summary}>
      <div class={styles.filterSummary} aria-live="polite">
        {props.summary}
      </div>
    </Show>
  </section>
);
